import React, { useEffect, useRef, useState } from "react"
import logo from "../../assets/img/logo.png"
import { useDispatch, connect } from "react-redux"
import { postLogin, postLoginReset } from "../../store/actions/navActions"
import { useHistory } from "react-router-dom"
import { isTokenValid } from "../../utilities/Auth"
import { COLORS } from "../../utilities/Constants"
import { APP_VERSION, APP_GIT_SHA } from "../../utilities/Addons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheck,
	faSync,
	faUser,
	faLock,
} from "@fortawesome/free-solid-svg-icons"

const styles = {
	container: {
		fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
		backgroundColor: COLORS.bg,
		minHeight: "100vh",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: 20,
		color: COLORS.text,
	},
	card: {
		width: 360,
		maxWidth: "90vw",
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 20,
		padding: "40px 36px",
		boxShadow: "0 12px 40px rgba(28, 25, 23, 0.06)",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		boxSizing: "border-box",
	},
	logo: {
		height: 56,
		objectFit: "contain",
		marginBottom: 18,
	},
	title: {
		fontSize: 24,
		fontWeight: 600,
		letterSpacing: "-0.02em",
		color: COLORS.text,
		margin: 0,
	},
	subtitle: {
		fontSize: 13,
		color: COLORS.textMuted,
		marginTop: 6,
		marginBottom: 28,
		textAlign: "center",
	},
	field: {
		position: "relative",
		width: "100%",
		marginBottom: 12,
	},
	inputIcon: {
		position: "absolute",
		left: 15,
		top: "50%",
		transform: "translateY(-50%)",
		color: COLORS.textLight,
		fontSize: 14,
		pointerEvents: "none",
	},
	input: {
		width: "100%",
		padding: "11px 14px 11px 42px",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 10,
		fontSize: 14,
		color: COLORS.text,
		backgroundColor: COLORS.surface,
		outline: "none",
		fontFamily: "inherit",
		boxSizing: "border-box",
		transition: "border-color 0.15s",
	},
	button: (disabled) => ({
		width: "100%",
		marginTop: 8,
		padding: "12px 24px",
		borderRadius: 10,
		border: "none",
		backgroundColor: COLORS.text,
		color: "#fff",
		fontSize: 14,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.45 : 1,
		transition: "opacity 0.15s",
	}),
	error: {
		fontSize: 12,
		fontWeight: 500,
		color: COLORS.danger,
		marginBottom: 12,
		minHeight: 16,
	},
	version: {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
	},
}

const Login = ({ navInfo }) => {
	const { error, loading, loaded } = navInfo.postLogin
	const { token } = navInfo.login
	const [username, setUsername] = useState("")
	const [password, setPassword] = useState("")
	const dispatch = useDispatch()
	const history = useHistory()
	const userRef = useRef(null)
	const pwRef = useRef(null)
	const connectUsername = () => {
		dispatch(postLogin({ username, password })).then((json) => {
			if (json && json.token && isTokenValid(json.token)) {
				history.push("/home")
			}
		})
	}
	const onKeyDown = (e, field) => {
		if (e.key === "Enter" && field === "pw") connectUsername()
		else if (e.key === "Enter" && field === "user") pwRef.current.focus()
	}
	useEffect(() => {
		if (error) {
			setTimeout(() => {
				dispatch(postLoginReset())
				setUsername("")
				setPassword("")
				userRef.current.focus()
			}, 750)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error])
	useEffect(() => {
		if (isTokenValid(token)) {
			history.push("/home")
			setUsername("")
			setPassword("")
		} else {
			userRef.current.focus()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const disabled =
		username === "" || password === "" || loading || loaded || error

	return (
		<div style={styles.container}>
			<form style={styles.card} onSubmit={(e) => e.preventDefault()}>
				<img src={logo} alt="SvpApp" style={styles.logo} />
				<h2 style={styles.title}>SvpApp</h2>
				<div style={styles.subtitle}>Connexion à l'espace de gestion</div>

				<div style={styles.field}>
					<FontAwesomeIcon icon={faUser} style={styles.inputIcon} />
					<input
						ref={userRef}
						type="text"
						placeholder="Utilisateur"
						value={username}
						onChange={(e) => setUsername(e.target.value)}
						onKeyDown={(e) => onKeyDown(e, "user")}
						onFocus={(e) =>
							(e.target.style.borderColor = COLORS.borderStrong)
						}
						onBlur={(e) =>
							(e.target.style.borderColor = COLORS.border)
						}
						autoComplete="off"
						disabled={error}
						required
						style={styles.input}
					/>
				</div>
				<div style={styles.field}>
					<FontAwesomeIcon icon={faLock} style={styles.inputIcon} />
					<input
						ref={pwRef}
						type="password"
						placeholder="Mot de passe"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						onKeyDown={(e) => onKeyDown(e, "pw")}
						onFocus={(e) =>
							(e.target.style.borderColor = COLORS.borderStrong)
						}
						onBlur={(e) =>
							(e.target.style.borderColor = COLORS.border)
						}
						autoComplete="new-password"
						disabled={error}
						required
						style={styles.input}
					/>
				</div>

				<div style={styles.error}>
					{error ? "Identifiants incorrects" : ""}
				</div>

				<button
					type="button"
					onClick={connectUsername}
					disabled={disabled}
					style={styles.button(disabled)}
				>
					{loading ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : loaded ? (
						<FontAwesomeIcon icon={faCheck} />
					) : (
						"Connecter"
					)}
				</button>
			</form>
			<div style={styles.version}>
				{`Ver. ${APP_VERSION}`}
				{APP_GIT_SHA ? ` (${APP_GIT_SHA})` : ""}
			</div>
		</div>
	)
}

const mapStateToProps = (state) => {
	return {
		navInfo: state.navInfo,
	}
}

export default connect(mapStateToProps)(Login)
